import { useEffect, type RefObject } from 'react';
import { usePresent } from './presentContext';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function visibleFocusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => getComputedStyle(el).display !== 'none');
}

export function trapTarget(items: HTMLElement[], root: HTMLElement, active: Node | null, shift: boolean): HTMLElement | null {
  const first = items[0];
  const last = items[items.length - 1];
  if (first === undefined || last === undefined) return root;
  if (!root.contains(active)) return shift ? last : first;
  if (shift && (active === first || active === root)) return last;
  if (!shift && active === last) return first;
  return null;
}

export function useModalFocus(ref: RefObject<HTMLElement | null>, onEscape: () => void): void {
  const present = usePresent();
  useEffect(() => {
    const root = ref.current;
    if (root === null || !present) return undefined;
    const opener = document.activeElement;
    root.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const target = trapTarget(visibleFocusables(root), root, document.activeElement, event.shiftKey);
      if (target === null) return;
      event.preventDefault();
      target.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Runs when the exit starts and again on unmount; by then focus may already belong to the next dialog.
      const active = document.activeElement;
      const ours = active === document.body || root.contains(active);
      if (ours && opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [ref, present]);
  // Its own effect: onEscape is usually a fresh closure, and re-running the focus effect would re-capture the opener.
  useEffect(() => {
    if (!present) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onEscape();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [present, onEscape]);
}
